import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import {
    CreditCardIcon,
    PlusIcon,
    PencilIcon,
    TrashIcon,
    CheckCircleIcon,
    XCircleIcon
} from '@heroicons/react/24/outline';

function PaymentMethodsPanel() {
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingMethod, setEditingMethod] = useState(null);
    const { token } = useAuth();
    
    const [formData, setFormData] = useState({
        slug: 'iyzico',
        displayName: 'İyzico',
        description: 'İyzico ödeme sistemi',
        isActive: true,
        isTestMode: true,
        priorityOrder: 0,
        commissionRate: 0,
        commissionType: 'percentage',
        api_key: '',
        secret_key: '',
        base_url: 'https://sandbox-api.iyzipay.com'
    });

    useEffect(() => {
        fetchPaymentMethods();
    }, []);

    const fetchPaymentMethods = async () => {
        setLoading(true);
        try {
            const response = await api.get('/api/payment-methods', {
                headers: { Authorization: `Bearer ${token}` }
            });
            // Backend response formatına göre array çıkar
            const methods = Array.isArray(response) ? response : (response.data || response.methods || []);
            setPaymentMethods(methods);
        } catch (error) {
            console.error('Ödeme yöntemleri getirme hatası:', error);
            toast.error('Ödeme yöntemleri yüklenemedi');
            setPaymentMethods([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.api_key || !formData.secret_key) {
            toast.warn('API Key ve Secret Key alanları zorunludur');
            return;
        }

        setLoading(true);
        try {
            // Backend formatına dönüştür
            const payload = {
                slug: formData.slug,
                displayName: formData.displayName,
                description: formData.description,
                isActive: formData.isActive,
                isTestMode: formData.isTestMode,
                priorityOrder: formData.priorityOrder,
                commissionRate: formData.commissionRate,
                commissionType: formData.commissionType,
                credentials: {
                    apiKey: formData.api_key,
                    secretKey: formData.secret_key,
                    baseUrl: formData.base_url
                }
            };
            
            if (editingMethod) {
                await api.put(`/api/payment-methods/${editingMethod.slug}`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Ödeme yöntemi güncellendi');
            } else {
                await api.post('/api/payment-methods', payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Ödeme yöntemi eklendi');
            }
            
            resetForm();
            fetchPaymentMethods();
        } catch (error) {
            console.error('Ödeme yöntemi kaydetme hatası:', error);
            toast.error(error.response?.data?.error || 'İşlem başarısız');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (method) => {
        setEditingMethod(method);
        setFormData({
            slug: method.slug,
            displayName: method.display_name,
            description: method.description || '',
            isActive: method.is_active,
            isTestMode: method.is_test_mode,
            priorityOrder: method.priority_order || 0,
            commissionRate: method.commission_rate || 0,
            commissionType: method.commission_type || 'percentage',
            api_key: '',
            secret_key: '',
            base_url: 'https://sandbox-api.iyzipay.com'
        });
        setShowForm(true);
    };

    const handleDelete = async (slug) => {
        if (!window.confirm('Bu ödeme yöntemini silmek istediğinizden emin misiniz?')) return;
        
        setLoading(true);
        try {
            await api.delete(`/api/payment-methods/${slug}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Ödeme yöntemi silindi');
            fetchPaymentMethods();
        } catch (error) {
            console.error('Silme hatası:', error);
            toast.error(error.response?.data?.error || 'Ödeme yöntemi silinemedi');
        } finally {
            setLoading(false);
        }
    };

    const toggleActive = async (slug, currentStatus) => {
        setLoading(true);
        try {
            await api.patch(`/api/payment-methods/${slug}/toggle`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(`Ödeme yöntemi ${!currentStatus ? 'aktif' : 'pasif'} edildi`);
            fetchPaymentMethods();
        } catch (error) {
            console.error('Durum güncelleme hatası:', error);
            toast.error('Durum güncellenemedi');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            slug: 'iyzico',
            displayName: 'İyzico',
            description: 'İyzico ödeme sistemi',
            isActive: true,
            isTestMode: true,
            priorityOrder: 0,
            commissionRate: 0,
            commissionType: 'percentage',
            api_key: '',
            secret_key: '',
            base_url: 'https://sandbox-api.iyzipay.com'
        });
        setEditingMethod(null);
        setShowForm(false);
    };

    if (loading && !showForm) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-green"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Ödeme Yöntemleri</h2>
                    <p className="text-sm text-gray-600 mt-1">İyzico ve diğer ödeme yöntemlerini yönetin</p>
                </div>
                <button
                    onClick={() => setShowForm(!showForm)}
                    className="flex items-center space-x-2 px-4 py-2 bg-brand-green text-white rounded-lg hover:bg-brand-green-light transition-colors"
                >
                    {showForm ? (
                        <>
                            <XCircleIcon className="w-5 h-5" />
                            <span>İptal</span>
                        </>
                    ) : (
                        <>
                            <PlusIcon className="w-5 h-5" />
                            <span>Yeni Ekle</span>
                        </>
                    )}
                </button>
            </div>

            {/* Form */}
            {showForm && (
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                    <h3 className="text-lg font-semibold mb-4">
                        {editingMethod ? 'Ödeme Yöntemini Düzenle' : 'Yeni Ödeme Yöntemi Ekle'}
                    </h3>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Slug */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Slug (unique ID)
                                </label>
                                <input
                                    type="text"
                                    value={formData.slug}
                                    onChange={(e) => setFormData({...formData, slug: e.target.value})}
                                    placeholder="iyzico"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                                    required
                                    disabled={!!editingMethod}
                                />
                            </div>

                            {/* Display Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Görünen Ad *
                                </label>
                                <input
                                    type="text"
                                    value={formData.displayName}
                                    onChange={(e) => setFormData({...formData, displayName: e.target.value})}
                                    placeholder="İyzico"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                                    required
                                />
                            </div>

                            {/* API Key */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    API Key *
                                </label>
                                <input
                                    type="text"
                                    value={formData.api_key}
                                    onChange={(e) => setFormData({...formData, api_key: e.target.value})}
                                    placeholder="sandbox-xxxxx"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                                    required
                                />
                            </div>

                            {/* Secret Key */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Secret Key *
                                </label>
                                <input
                                    type="password"
                                    value={formData.secret_key}
                                    onChange={(e) => setFormData({...formData, secret_key: e.target.value})}
                                    placeholder={editingMethod ? "Değiştirmek için girin" : "sandbox-xxxxx"}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                                    required={!editingMethod}
                                />
                                {editingMethod && (
                                    <p className="text-xs text-gray-500 mt-1">Boş bırakırsanız mevcut key korunur</p>
                                )}
                            </div>

                            {/* Base URL */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Base URL
                                </label>
                                <input
                                    type="text"
                                    value={formData.base_url}
                                    onChange={(e) => setFormData({...formData, base_url: e.target.value})}
                                    placeholder="https://sandbox-api.iyzipay.com"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                                />
                            </div>

                            {/* Commission Rate */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Komisyon Oranı (%)
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={formData.commissionRate}
                                    onChange={(e) => setFormData({...formData, commissionRate: parseFloat(e.target.value)})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                                />
                            </div>

                            {/* Description */}
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Açıklama
                                </label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                    placeholder="İyzico ödeme sistemi"
                                    rows={2}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                                />
                            </div>
                        </div>

                        {/* Checkboxes */}
                        <div className="flex items-center space-x-6">
                            <label className="flex items-center space-x-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.isTestMode}
                                    onChange={(e) => setFormData({...formData, isTestMode: e.target.checked})}
                                    className="w-4 h-4 text-brand-green focus:ring-brand-green rounded"
                                />
                                <span className="text-sm text-gray-700">Test Modu</span>
                            </label>

                            <label className="flex items-center space-x-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.isActive}
                                    onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                                    className="w-4 h-4 text-brand-green focus:ring-brand-green rounded"
                                />
                                <span className="text-sm text-gray-700">Aktif</span>
                            </label>
                        </div>

                        {/* Buttons */}
                        <div className="flex justify-end space-x-3 pt-4">
                            <button
                                type="button"
                                onClick={resetForm}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                İptal
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-6 py-2 bg-brand-green text-white rounded-lg hover:bg-brand-green-light transition-colors disabled:bg-gray-400"
                            >
                                {loading ? 'Kaydediliyor...' : (editingMethod ? 'Güncelle' : 'Kaydet')}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Payment Methods List */}
            <div className="grid grid-cols-1 gap-4">
                {paymentMethods.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                        <CreditCardIcon className="w-16 h-16 mx-auto text-gray-400 mb-3" />
                        <p className="text-gray-600">Henüz ödeme yöntemi eklenmemiş</p>
                        <p className="text-sm text-gray-500 mt-1">Yeni bir ödeme yöntemi eklemek için yukarıdaki butona tıklayın</p>
                    </div>
                ) : (
                    paymentMethods.map((method) => (
                        <div
                            key={method.id}
                            className="bg-white rounded-xl shadow-md p-6 border border-gray-200 hover:shadow-lg transition-shadow"
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex items-start space-x-4 flex-1">
                                    <div className="w-12 h-12 bg-brand-green bg-opacity-10 rounded-lg flex items-center justify-center">
                                        <CreditCardIcon className="w-6 h-6 text-brand-green" />
                                    </div>
                                    
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-2 mb-2">
                                            <h3 className="text-lg font-semibold text-gray-900">
                                                {method.display_name || method.slug}
                                            </h3>
                                            {method.is_active ? (
                                                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                                                    Aktif
                                                </span>
                                            ) : (
                                                <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                                                    Pasif
                                                </span>
                                            )}
                                            {method.is_test_mode && (
                                                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                                                    Test Modu
                                                </span>
                                            )}
                                        </div>
                                        
                                        <div className="space-y-1 text-sm text-gray-600">
                                            <p><span className="font-medium">Slug:</span> {method.slug}</p>
                                            {method.description && (
                                                <p><span className="font-medium">Açıklama:</span> {method.description}</p>
                                            )}
                                            <p><span className="font-medium">Komisyon:</span> %{method.commission_rate || 0}</p>
                                            <p className="text-xs text-gray-400">
                                                Oluşturulma: {new Date(method.created_at).toLocaleDateString('tr-TR')}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex items-center space-x-2">
                                    <button
                                        onClick={() => toggleActive(method.slug, method.is_active)}
                                        className={`p-2 rounded-lg transition-colors ${
                                            method.is_active
                                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                                : 'bg-red-100 text-red-700 hover:bg-red-200'
                                        }`}
                                        title={method.is_active ? 'Pasif Yap' : 'Aktif Yap'}
                                    >
                                        {method.is_active ? (
                                            <CheckCircleIcon className="w-5 h-5" />
                                        ) : (
                                            <XCircleIcon className="w-5 h-5" />
                                        )}
                                    </button>
                                    
                                    <button
                                        onClick={() => handleEdit(method)}
                                        className="p-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                                        title="Düzenle"
                                    >
                                        <PencilIcon className="w-5 h-5" />
                                    </button>
                                    
                                    <button
                                        onClick={() => handleDelete(method.slug)}
                                        className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                                        title="Sil"
                                    >
                                        <TrashIcon className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

export default PaymentMethodsPanel;
